/**
 * Optimized Master Data Hooks
 *
 * These hooks replace the monolithic MastersContext by providing selective data loading.
 * Only load the collections you need, reducing Firestore reads and memory usage.
 *
 * Features:
 * - Selective loading (only load what you need)
 * - React Query caching (5-minute cache, reduces redundant reads)
 * - Optional real-time listeners (opt-in for critical data)
 * - TypeScript type safety (no more 'any' types)
 * - Automatic error handling and retry logic
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { collection, getDocs, onSnapshot, query, Query, where } from 'firebase/firestore';
import { db } from '../firebase';
import {
  BusinessType,
  BusinessUnit,
  CostCenter,
  Item,
  Shop,
  Vendor
} from '../types/masterData';
import { ExpenseCategory } from '../types/expenseCategory';
import { useEffect, useState, useCallback } from 'react';

// Master data query keys for cache management
export const masterDataKeys = {
  types: ['masterData', 'types'] as const,
  units: ['masterData', 'units'] as const,
  unitsByType: (typeId: string) => ['masterData', 'units', 'byType', typeId] as const,
  costCenters: ['masterData', 'costCenters'] as const,
  categories: ['masterData', 'categories'] as const,
  categoriesByType: (typeId: string) => ['masterData', 'categories', 'byType', typeId] as const,
  items: ['masterData', 'items'] as const,
  shops: ['masterData', 'shops'] as const,
  vendors: ['masterData', 'vendors'] as const,
};

// Default query options for master data (5-minute cache)
const defaultMasterDataOptions: Partial<UseQueryOptions> = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
  refetchOnWindowFocus: false, // Don't refetch on window focus (data changes infrequently)
  retry: 2,
};

/**
 * Hook to load business types (Mill, Vehicle, etc.)
 * Typically 2-5 documents, rarely changes
 */
export function useBusinessTypes(options?: Partial<UseQueryOptions<BusinessType[]>>) {
  return useQuery<BusinessType[]>({
    queryKey: masterDataKeys.types,
    queryFn: async () => {
      const snapshot = await getDocs(collection(db, 'types'));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as BusinessType));
    },
    ...defaultMasterDataOptions,
    ...options,
  });
}

/**
 * Hook to load business units (Unit 1, Unit 2, etc.)
 * Can filter by type if needed
 */
export function useBusinessUnits(
  typeId?: string,
  options?: Partial<UseQueryOptions<BusinessUnit[]>>
) {
  const queryKey = typeId
    ? masterDataKeys.unitsByType(typeId)
    : masterDataKeys.units;

  return useQuery<BusinessUnit[]>({
    queryKey,
    queryFn: async () => {
      let q = collection(db, 'units');

      if (typeId) {
        q = query(collection(db, 'units'), where('typeId', '==', typeId)) as any;
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as BusinessUnit));
    },
    ...defaultMasterDataOptions,
    ...options,
  });
}

/**
 * Hook to load cost centers
 */
export function useCostCenters(options?: Partial<UseQueryOptions<CostCenter[]>>) {
  return useQuery<CostCenter[]>({
    queryKey: masterDataKeys.costCenters,
    queryFn: async () => {
      const snapshot = await getDocs(collection(db, 'costCenters'));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as CostCenter));
    },
    ...defaultMasterDataOptions,
    ...options,
  });
}

/**
 * Hook to load expense categories
 * Can filter by type if needed
 */
export function useCategories(
  optionsOrTypeId?: string | Partial<UseQueryOptions<ExpenseCategory[]>>,
  maybeOptions?: Partial<UseQueryOptions<ExpenseCategory[]>>
) {
  // Handle both call signatures:
  // useCategories() - no params
  // useCategories({ enabled: true }) - options only
  // useCategories('typeId123') - typeId only
  // useCategories('typeId123', { enabled: true }) - both params
  
  let typeId: string | undefined;
  let options: Partial<UseQueryOptions<ExpenseCategory[]>> | undefined;
  
  if (typeof optionsOrTypeId === 'string') {
    typeId = optionsOrTypeId;
    options = maybeOptions;
  } else {
    typeId = undefined;
    options = optionsOrTypeId;
  }

  const queryKey = typeId
    ? masterDataKeys.categoriesByType(typeId)
    : masterDataKeys.categories;

  return useQuery<ExpenseCategory[]>({
    queryKey,
    queryFn: async () => {
      console.log('[useCategories] Starting query for categories collection...');
      let q = collection(db, 'categories');

      if (typeId) {
        console.log('[useCategories] Filtering by typeId:', typeId);
        q = query(collection(db, 'categories'), where('typeId', '==', typeId)) as any;
      } else {
        console.log('[useCategories] Loading ALL categories (no typeId filter)');
      }

      try {
        const snapshot = await getDocs(q);
        console.log('[useCategories] Query successful! Document count:', snapshot.size);
        
        const categories = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as ExpenseCategory));
        
        console.log('[useCategories] Mapped categories:', categories);
        return categories;
      } catch (error) {
        console.error('[useCategories] ERROR fetching categories:', error);
        throw error;
      }
    },
    ...defaultMasterDataOptions,
    ...options,
  });
}

/**
 * Hook to load items (stock/capital items)
 */
export function useItems(options?: Partial<UseQueryOptions<Item[]>>) {
  return useQuery<Item[]>({
    queryKey: masterDataKeys.items,
    queryFn: async () => {
      const snapshot = await getDocs(collection(db, 'items'));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Item));
    },
    ...defaultMasterDataOptions,
    ...options,
  });
}

/**
 * Hook to load shops
 */
export function useShops(options?: Partial<UseQueryOptions<Shop[]>>) {
  return useQuery<Shop[]>({
    queryKey: masterDataKeys.shops,
    queryFn: async () => {
      const snapshot = await getDocs(collection(db, 'shops'));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Shop));
    },
    ...defaultMasterDataOptions,
    ...options,
  });
}

/**
 * Hook to load vendors
 */
export function useVendors(options?: Partial<UseQueryOptions<Vendor[]>>) {
  return useQuery<Vendor[]>({
    queryKey: masterDataKeys.vendors,
    queryFn: async () => {
      const snapshot = await getDocs(collection(db, 'vendors'));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Vendor));
    },
    ...defaultMasterDataOptions,
    ...options,
  });
}

/**
 * Hook for real-time master data (opt-in for critical collections)
 * Use this when you need real-time updates (e.g., in settings pages)
 *
 * Example:
 * const { data: types } = useLiveMasterData('types');
 */
export function useLiveMasterData<T = any>(
  collectionName: string,
  enabled: boolean = true
) {
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const unsubscribe = onSnapshot(
      collection(db, collectionName),
      (snapshot) => {
        const items = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as T[];
        setData(items);
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        console.error(`Error loading ${collectionName}:`, err);
        setError(err as Error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [collectionName, enabled]);

  return { data, isLoading, error };
}

/**
 * Utility hook to prefetch all master data at once
 * Use this in the root component to warm up the cache
 * Much more efficient than MastersContext's approach
 */
export function usePrefetchMasterData() {
  const prefetchTypes = useBusinessTypes();
  const prefetchUnits = useBusinessUnits();
  const prefetchCostCenters = useCostCenters();
  const prefetchCategories = useCategories();
  // Don't prefetch items, shops, vendors unless actually needed

  const isLoading =
    prefetchTypes.isLoading ||
    prefetchUnits.isLoading ||
    prefetchCostCenters.isLoading ||
    prefetchCategories.isLoading;

  return { isLoading };
}

/**
 * Backward compatibility hook for components still using useMasters()
 * This provides the same interface as MastersContext but with optimized loading
 *
 * NOTE: Use individual hooks instead for better performance!
 * This hook loads ALL collections like the old MastersContext.
 */
export function useMasters() {
  const { data: types = [] } = useBusinessTypes();
  const { data: units = [] } = useBusinessUnits();
  const { data: costCenters = [] } = useCostCenters();
  const { data: categories = [] } = useCategories();
  const { data: items = [] } = useItems();
  const { data: shops = [] } = useShops();
  const { data: vendors = [] } = useVendors();

  return {
    types,
    units,
    costCenters,
    categories,
    items,
    shops,
    vendors,
  };
}

/**
 * Helper hooks for common lookup operations
 */

export function useTypeName(typeId: string | undefined): string {
  const { data: types = [] } = useBusinessTypes();
  if (!typeId) return '';
  const type = types.find(t => t.id === typeId);
  return type?.name || '';
}

export function useUnitName(unitId: string | undefined): string {
  const { data: units = [] } = useBusinessUnits();
  if (!unitId) return '';
  const unit = units.find(u => u.id === unitId);
  return unit?.name || '';
}

export function useCostCenterName(costCenterId: string | undefined): string {
  const { data: costCenters = [] } = useCostCenters();
  if (!costCenterId) return '';
  const costCenter = costCenters.find(cc => cc.id === costCenterId);
  return costCenter?.name || '';
}

export function useCategoryName(categoryId: string | undefined): string {
  const { data: categories = [] } = useCategories();
  if (!categoryId) return '';
  const category = categories.find(c => c.id === categoryId);
  return category?.name || '';
}

export function useShopName(shopId: string | undefined): string {
  const { data: shops = [] } = useShops();
  if (!shopId) return '';
  const shop = shops.find(s => s.id === shopId);
  return shop?.name || '';
}

export function useVendorName(vendorId: string | undefined): string {
  const { data: vendors = [] } = useVendors();
  if (!vendorId) return '';
  const vendor = vendors.find(v => v.id === vendorId);
  return vendor?.name || '';
}
